import { useLive, formatPhone, STAGE_LABEL } from "../lib/api";
import type { LiveConversation, Stage } from "../lib/api";
import { Card, Score, Pill, Empty, Carregando, Avatar } from "../lib/ui";
import { MessagesSquare, Sparkles, Keyboard, ClipboardList, Repeat, Coins, Radio } from "lucide-react";

/**
 * O que está acontecendo agora, em cada conversa.
 *
 * A pergunta que essa tela responde é "o robô está fazendo o quê?" — não
 * "quantos leads eu tenho". Por isso o que ordena é urgência e atividade, e
 * cada linha diz o estado em palavras, não em código.
 */
export function AoVivo({ onOpenLead }: { onOpenLead: (id: string) => void }) {
  const { data } = useLive<LiveConversation[]>("/live", [
    "messages",
    "conversations",
    "leads",
    "agent_turns",
    "handoffs",
  ]);

  if (!data) return <Carregando />;
  if (data.length === 0) {
    return <Empty icone={Radio}>Nenhuma conversa ativa nas últimas 48h.</Empty>;
  }

  const pensando = data.filter((c) => c.thinking_since).length;
  const digitando = data.filter((c) => c.presence).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Pill>
          <MessagesSquare size={12} />
          {data.length} conversa{data.length > 1 ? "s" : ""} ativa{data.length > 1 ? "s" : ""}
        </Pill>
        {pensando > 0 && (
          <Pill tone="accent">
            <Sparkles size={12} />
            {pensando} sendo respondida{pensando > 1 ? "s" : ""} agora
          </Pill>
        )}
        {digitando > 0 && (
          <Pill tone="ok">
            <Keyboard size={12} />
            {digitando} digitando
          </Pill>
        )}
      </div>

      <div className="space-y-2">
        {data.map((c) => (
          <Linha key={c.conversation_id} c={c} onOpen={() => onOpenLead(c.lead_id)} />
        ))}
      </div>
    </div>
  );
}

function Linha({ c, onOpen }: { c: LiveConversation; onOpen: () => void }) {
  const estado = descreverEstado(c);

  return (
    <Card
      stripe={estado.stripe}
      className="px-4 py-3.5 cursor-pointer hover:border-rule-2 hover:bg-surface-2/50 transition-colors"
    >
      <div onClick={onOpen} className="flex gap-3">
        <Avatar nome={c.lead_name} />
        <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-ink font-semibold">{c.lead_name ?? "Sem nome"}</span>
          <span className="text-xs tabular-nums text-ink-3">
            {formatPhone(c.lead_phone)}
          </span>
          <Pill>{STAGE_LABEL[c.stage as Stage] ?? c.stage}</Pill>
          <span className="text-xs text-ink-3">via {c.channel_label}</span>

          <span
            className={`ml-auto text-xs font-medium flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface-2 ${estado.cor}`}
          >
            {estado.pulsa && <span className="pulse text-[8px]">●</span>}
            {estado.texto}
          </span>
        </div>

        {c.last_message && (
          <p className="text-sm text-ink-2 truncate">
            <span className="text-ink-3 text-xs mr-1.5">
              {c.last_message_author === "lead"
                ? "lead:"
                : c.last_message_author === "human"
                  ? "corretor:"
                  : "IA:"}
            </span>
            {c.last_message}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-3">
          <span className="flex items-center gap-1.5">
            <ClipboardList size={12} />
            {c.facts_count} fatos
          </span>
          <span className="flex items-center gap-1.5">
            <Repeat size={12} />
            {c.turns_count} turnos
          </span>
          {c.usd != null && c.usd > 0 && (
            <span className="flex items-center gap-1.5 tabular-nums">
              <Coins size={12} />
              US$ {Number(c.usd).toFixed(3)}
            </span>
          )}
          <div className="ml-auto flex items-center gap-5 w-80">
            <Score value={c.fit_score} label="fit" />
            <Score value={c.temp_score} label="temp" tone="warm" />
          </div>
        </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Traduz o estado da conversa para o que uma pessoa diria.
 *
 * A ordem das checagens é a ordem da urgência: o que precisa de gente vem
 * antes do que está acontecendo, que vem antes do que está esperando.
 */
function descreverEstado(c: LiveConversation): {
  texto: string;
  cor: string;
  stripe?: "accent" | "warm" | "danger" | "ok";
  pulsa?: boolean;
} {
  if (c.control === "pending_human") {
    return {
      texto: "esperando um corretor",
      cor: "text-warm",
      stripe: "warm",
      pulsa: true,
    };
  }
  if (c.control === "human") {
    return {
      texto: `${c.assigned_broker ?? "corretor"} atendendo`,
      cor: "text-accent",
      stripe: "accent",
    };
  }
  if (c.thinking_since) {
    return {
      texto: "a IA está escrevendo a resposta",
      cor: "text-accent",
      stripe: "accent",
      pulsa: true,
    };
  }
  if (c.presence) {
    return {
      texto: c.presence === "recording" ? "gravando áudio" : "digitando",
      cor: "text-ok",
      stripe: "ok",
      pulsa: true,
    };
  }
  if (c.last_message_author === "lead") {
    // Lead falou e ninguém respondeu ainda. Se passou de um minuto, algo
    // travou — normalmente é o debounce ou o worker fora do ar.
    const s = c.quiet_seconds ?? 0;
    return s > 60
      ? { texto: `sem resposta há ${duracao(s)}`, cor: "text-danger", stripe: "danger" }
      : { texto: "aguardando o debounce", cor: "text-ink-3" };
  }
  if (c.followup_at) {
    const min = Math.round(
      (new Date(c.followup_at).getTime() - Date.now()) / 60_000,
    );
    return {
      texto: min <= 0 ? "toque saindo agora" : `próximo toque em ${duracao(min * 60)}`,
      cor: "text-ink-3",
    };
  }
  return {
    texto: `em silêncio há ${duracao(c.quiet_seconds ?? 0)}`,
    cor: "text-ink-3",
  };
}

function duracao(segundos: number): string {
  if (segundos < 60) return `${Math.round(segundos)}s`;
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min}min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}
